import { useMemo, useState } from 'react';
import { useUsers } from '../api/hooks';
import { Modal } from '../components/ui';

/**
 * Assigns a batch of leads to one or more people.
 *
 * With several people the leads are dealt out in turn, in the order they were
 * selected, so the split is even to within one lead. Each person's share then
 * goes through the ordinary bulk-assign call.
 */
export function BulkAssignModal({
  count,
  onAssign,
  onClose,
  busy,
}: {
  count: number;
  onAssign: (userIds: string[]) => void;
  onClose: () => void;
  busy: boolean;
}) {
  const { data: users = [], isPending } = useUsers(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => u.full_name.toLowerCase().includes(q) || (u.role?.name ?? '').toLowerCase().includes(q),
    );
  }, [users, query]);

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const per = selected.length > 0 ? Math.floor(count / selected.length) : 0;
  const extra = selected.length > 0 ? count % selected.length : 0;

  return (
    <Modal
      title={`Assign ${count} lead${count === 1 ? '' : 's'}`}
      onClose={onClose}
      width={520}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={selected.length === 0 || busy}
            onClick={() => onAssign(selected)}
          >
            {busy ? (
              <span className="spinner" />
            ) : selected.length > 1 ? (
              `Assign to ${selected.length} people`
            ) : (
              'Assign'
            )}
          </button>
        </>
      }
    >
      <div className="team-choice-head">
        <span>Assign to — choose one or more people</span>
        {selected.length > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected([])}>
            Clear
          </button>
        )}
      </div>

      {users.length > 6 && (
        <div className="field" style={{ marginBottom: 8 }}>
          <input
            placeholder="Search people…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
      )}

      {isPending ? (
        <p className="hint" style={{ margin: 0 }}>Loading people…</p>
      ) : (
        <div className="team-choices" style={{ maxHeight: 320, overflowY: 'auto' }}>
          {visible.map((u) => (
            <label key={u.id} className="team-choice">
              <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} />
              <span className="team-choice-body">
                <strong>{u.full_name}</strong>
                {u.role && <span className="team-choice-meta">{u.role.name}</span>}
              </span>
            </label>
          ))}
          {visible.length === 0 && <p className="hint" style={{ margin: 0 }}>No one matches.</p>}
        </div>
      )}

      <p className="hint">
        {selected.length > 1
          ? `Split evenly in turn: each person gets ${per}${extra > 0 ? ` or ${per + 1}` : ''} lead${per === 1 && extra === 0 ? '' : 's'}. `
          : ''}
        Every selected lead moves to its new owner, whatever team it currently belongs to.
      </p>
    </Modal>
  );
}
